import { useState } from "react";
import { Link } from "react-router-dom";
import { RefreshCw, PackageCheck, BellOff } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function Admin() {
  const [key, setKey] = useState(() => sessionStorage.getItem("arij-admin-key") || "");
  const [orders, setOrders] = useState(null);
  const [err, setErr] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = async (k) => {
    setErr("");
    try {
      const r = await fetch(`${API}/admin/orders`, { headers: { "x-admin-key": k } });
      if (!r.ok) {
        setOrders(null);
        setErr(r.status === 401 ? "Clé invalide." : "Erreur " + r.status);
        return;
      }
      sessionStorage.setItem("arij-admin-key", k);
      setOrders(await r.json());
    } catch {
      setErr("Connexion impossible.");
    }
  };

  const retryNotification = async (id) => {
    setBusyId(id);
    try {
      await fetch(`${API}/admin/orders/${id}/resend-notification`, { method: "POST", headers: { "x-admin-key": key } });
      await load(key);
    } catch {}
    setBusyId(null);
  };

  if (orders === null) {
    return (
      <div className="pt-40 pb-32 min-h-screen flex flex-col items-center justify-center px-5" data-testid="admin-login">
        <p className="font-display text-2xl tracking-[0.35em] text-gold">ARIJ&nbsp;MISK</p>
        <h1 className="font-display text-2xl text-ivory mt-4">Commandes — accès administration</h1>
        <input
          data-testid="admin-key-input"
          type="password"
          value={key}
          onChange={(e) => setKey(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load(key)}
          placeholder="Clé d'administration"
          className="mt-6 w-80 max-w-full bg-onyx/70 border border-gold/25 rounded-full px-5 py-3 text-sm text-ivory placeholder:text-ivory/35 focus:border-gold/70"
        />
        <button onClick={() => load(key)} data-testid="admin-login-button" className="btn-gold rounded-full px-8 py-3 text-[0.7rem] tracking-[0.25em] uppercase font-semibold mt-4">
          Ouvrir les commandes
        </button>
        {err && <p className="text-red-300/80 text-sm mt-4" data-testid="admin-error">{err}</p>}
      </div>
    );
  }

  return (
    <div className="pt-32 pb-24 star-lattice min-h-screen" data-testid="admin-page">
      <div className="max-w-5xl mx-auto px-5 lg:px-8">
        <h1 className="font-display text-4xl text-ivory">Commandes ({orders.length})</h1>
        <div className="mt-10 space-y-5">
          {orders.map((o) => (
            <div key={o.id} className="border border-gold/20 rounded-2xl p-6 bg-onyx/60" data-testid={"admin-order-" + o.id}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="font-display text-xl text-gold" data-testid="admin-order-id">{o.id.slice(0, 8)}…</p>
                  <p className="text-[0.65rem] tracking-[0.2em] uppercase text-sand mt-1">{(o.created_at || "").slice(0, 19).replace("T", " ")}</p>
                </div>
                <div className="text-right">
                  <p className={"text-sm font-semibold " + (o.payment_status === "paid" ? "text-emerald-300/90" : "text-ivory/60")} data-testid="admin-order-status">{o.status} · {o.payment_status}</p>
                  <p className="font-display text-lg text-ivory mt-1">{o.total?.toFixed?.(2) ?? o.total} $ CAD</p>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 mt-5 text-sm">
                <div className="text-ivory/80">
                  <p><strong className="text-gold/80">Client :</strong> {o.customer_name || "—"}</p>
                  <p><strong className="text-gold/80">Courriel :</strong> {o.customer_email || "—"}</p>
                  <p><strong className="text-gold/80">Téléphone :</strong> {o.phone || "—"}</p>
                  <p><strong className="text-gold/80">Destination :</strong> {o.shipping_address?.destination || o.destination || "—"}</p>
                </div>
                <div className="text-ivory/80">
                  <p><strong className="text-gold/80">Adresse :</strong></p>
                  <p>{o.shipping_address?.address || "—"}{o.shipping_address?.apt ? ", " + o.shipping_address.apt : ""}</p>
                  <p>{o.shipping_address?.city || ""} {o.shipping_address?.province || ""} {o.shipping_address?.postal || ""}</p>
                  <p>{o.shipping_address?.country || ""}</p>
                </div>
              </div>

              <ul className="mt-4 text-sm text-ivory/85 space-y-1">
                {(o.items_full || o.items || []).map((it, j) => (
                  <li key={j} data-testid="admin-order-item">· {it.name || it.slug} × {it.qty} — {((it.price || 0) * (it.qty || 0)).toFixed(2)} $ {(it.price ? "(unit. " + Number(it.price).toFixed(2) + " $)" : "")}</li>
                ))}
              </ul>

              <div className="flex items-center justify-between gap-3 mt-4 pt-3 border-t border-gold/15">
                <p className={"text-[0.7rem] flex items-center gap-1.5 " + ((o.notification || {}).status === "sent" ? "text-emerald-300/80" : (o.notification || {}).status === "failed" ? "text-red-300/80" : "text-sand")}>
                  {(o.notification || {}).status === "sent" ? <PackageCheck className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
                  Notification : {(o.notification || {}).status || "aucune"}{(o.notification || {}).last_error ? " — " + o.notification.last_error.slice(0, 80) : ""}
                </p>
                <button
                  data-testid={"admin-retry-" + o.id}
                  onClick={() => retryNotification(o.id)}
                  disabled={busyId === o.id}
                  className="btn-ghost rounded-full px-4 py-1.5 text-[0.6rem] tracking-[0.2em] uppercase flex items-center gap-1.5"
                >
                  <RefreshCw className={"w-3 h-3 " + (busyId === o.id ? "animate-spin" : "")} /> Renvoyer le courriel
                </button>
              </div>
            </div>
          ))}
        </div>
        <Link to="/" className="inline-block mt-10 text-[0.65rem] tracking-[0.25em] uppercase text-ivory/50 hover:text-gold">← ARIJ MISK</Link>
      </div>
    </div>
  );
}
